import type { RfqPackage, RfqProject, RfqSubmission } from '../types';
import type { PurchaseJobPlBucket, PurchasingPolicy } from '../types/purchasing';
import { uploadLegacyQuoteFile, validateLegacyQuoteFile } from './legacyQuoteFileUtils';
import { isValidQuoteTotal } from './rfqQuoteTotal';
import {
  filterPackagesForReleaseSelection,
  PRE_PORTAL_RELEASE_ID,
  PRE_PORTAL_RELEASE_VERSION_INDEX,
} from './timberRfqPrePortalVersion';
import { rfqProjectSupplierOrgIds } from './rfqProjectSuppliers';
import { otherQuoteVendorOrgId } from './otherQuoteVendor';
import {
  assertOtherQuoteJobPurchaseReady,
  isOtherQuotePayMethodId,
  OTHER_QUOTE_PAYMENT_METHODS,
  type OtherQuotePayMethodId,
  type SubmitOtherQuoteJobPurchaseInput,
  type SubmitOtherQuoteJobPurchaseResult,
} from './otherQuotePurchase';
import { isPurchaseJobPlBucket } from './purchasingSpendClass';
import type { ApprovalResolverUser } from './purchasingApprovals';
import type { CRMUser } from '../types';

export {
  isOtherQuoteExternalVendorOrgId,
  otherQuoteVendorNameFromOrgId,
  otherQuoteVendorOrgId,
  OTHER_QUOTE_VENDOR_ORG_PREFIX,
  resolveOtherQuoteSupplierName,
} from './otherQuoteVendor';

export type OtherQuoteUploadInput = {
  file: File;
  quoteLabel: string;
  quoteTotal: number;
  quoteValidUntil?: Date | null;
  notes?: string;
  receivedAt?: Date;
  plBucket: PurchaseJobPlBucket;
  paymentMethodId: OtherQuotePayMethodId;
  paymentTermsDraft?: import('./supplierBills').SupplierPaymentTermsSpec;
};

export type OtherQuoteUploadDeps = {
  createRfqProject: (row: Omit<RfqProject, 'id' | 'createdAt'>) => Promise<string>;
  publishRfqPackage: (input: {
    rfqProjectId: string;
    supplierOrgId: string;
    targetSupplierOrgIds?: string[];
    dealId: string;
    dealTitle?: string;
    workType?: 'other_quote';
    label?: string;
    releaseDisplayLabel?: string;
    legacyImport?: boolean;
    releaseVersionIndex?: number;
    createdByUid: string;
    createdByName: string;
  }) => Promise<string[]>;
  createRfqSubmission: (
    row: Omit<import('../types').RfqSubmission, 'id' | 'createdAt'>,
    options?: { createdAt?: Date },
  ) => Promise<string>;
  updateRfqProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  updateRfqSubmission: (id: string, updates: Partial<RfqSubmission>) => Promise<void>;
  submitLinkedJobPurchase?: (
    input: SubmitOtherQuoteJobPurchaseInput,
  ) => Promise<SubmitOtherQuoteJobPurchaseResult>;
};

export type EnsureOtherQuoteProjectInput = {
  dealId: string;
  dealTitle?: string;
  timberProjectId?: string;
  timberProjectName?: string;
  vendorName: string;
  staffUserId: string;
  staffUserName: string;
};

export async function ensureOtherQuoteRfqProject(
  deps: Pick<OtherQuoteUploadDeps, 'createRfqProject' | 'updateRfqProject'>,
  existing: RfqProject | null,
  input: EnsureOtherQuoteProjectInput,
): Promise<RfqProject> {
  const vendorName = input.vendorName.trim();
  if (!vendorName) throw new Error('Enter a supplier name.');
  const orgId = otherQuoteVendorOrgId(vendorName);

  if (existing?.id) {
    const roster = rfqProjectSupplierOrgIds(existing);
    if (!roster.includes(orgId)) {
      await deps.updateRfqProject(existing.id, {
        supplierOrgIds: [...roster, orgId],
        supplierOrgId: existing.supplierOrgId || orgId,
      });
    }
    return {
      ...existing,
      supplierOrgIds: roster.includes(orgId) ? roster : [...roster, orgId],
    };
  }

  const id = await deps.createRfqProject({
    dealId: input.dealId,
    dealTitle: input.dealTitle,
    timberProjectId: input.timberProjectId,
    timberProjectName: input.timberProjectName,
    workType: 'other_quote',
    supplierOrgId: orgId,
    supplierOrgIds: [orgId],
    status: 'active',
    createdByUid: input.staffUserId,
    createdByName: input.staffUserName,
  });

  return {
    id,
    dealId: input.dealId,
    dealTitle: input.dealTitle,
    timberProjectId: input.timberProjectId,
    timberProjectName: input.timberProjectName,
    workType: 'other_quote',
    supplierOrgId: orgId,
    supplierOrgIds: [orgId],
    status: 'active',
    createdAt: new Date(),
  };
}

export function findOtherQuotePackageForOrg(
  packages: RfqPackage[],
  projectId: string,
  orgId: string,
): RfqPackage | undefined {
  const scoped = filterPackagesForReleaseSelection(
    packages.filter((p) => p.rfqProjectId === projectId && p.supplierOrgId === orgId),
    PRE_PORTAL_RELEASE_ID,
  );
  return scoped[0];
}

export async function ensureOtherQuotePackage(
  deps: Pick<OtherQuoteUploadDeps, 'publishRfqPackage'>,
  project: RfqProject,
  packages: RfqPackage[],
  orgId: string,
  quoteLabel: string,
  staffUserId: string,
  staffUserName: string,
): Promise<string> {
  const projectId = project.id;
  if (!projectId) throw new Error('RFQ project is missing.');

  const existing = findOtherQuotePackageForOrg(packages, projectId, orgId);
  if (existing?.id) return existing.id;

  const label = quoteLabel.trim() || 'Other quote';
  const ids = await deps.publishRfqPackage({
    rfqProjectId: projectId,
    supplierOrgId: orgId,
    targetSupplierOrgIds: [orgId],
    dealId: project.dealId,
    dealTitle: project.dealTitle,
    workType: 'other_quote',
    label,
    releaseDisplayLabel: label,
    legacyImport: true,
    releaseVersionIndex: PRE_PORTAL_RELEASE_VERSION_INDEX,
    createdByUid: staffUserId,
    createdByName: staffUserName,
  });
  if (!ids[0]) throw new Error('Could not create supplier package for this quote.');
  return ids[0];
}

export function formatOtherQuoteLabel(sub: Pick<import('../types').RfqSubmission, 'quoteLabel' | 'fileName'>): string {
  return sub.quoteLabel?.trim() || sub.fileName?.replace(/\.pdf$/i, '').trim() || 'Quote';
}

/** Confirm-dialog name for timber, CNC, and other-quote submissions. */
export function rfqSubmissionDeleteLabel(
  sub: Pick<import('../types').RfqSubmission, 'quoteLabel' | 'quotedSpecies' | 'fileName'>,
): string {
  return (
    sub.quoteLabel?.trim() ||
    sub.quotedSpecies?.trim() ||
    sub.fileName?.replace(/\.pdf$/i, '').trim() ||
    'this quote'
  );
}

export type OtherQuoteSubmissionResult = {
  submissionId: string;
  purchaseRequisitionId: string;
  issued: boolean;
  paymentMethodId: OtherQuotePayMethodId;
  paymentMethod: string;
};

export async function createOtherQuoteSubmission(
  deps: OtherQuoteUploadDeps,
  args: {
    project: RfqProject;
    packages: RfqPackage[];
    orgId: string;
    orgName: string;
    input: OtherQuoteUploadInput;
    staffUserId: string;
    staffUserName: string;
    dealTitle?: string;
    timberProjectId?: string;
    projectManagerUserId?: string | null;
    users: ApprovalResolverUser[] | CRMUser[];
    policy: PurchasingPolicy;
  },
): Promise<OtherQuoteSubmissionResult> {
  const { project, packages, orgId, orgName, input, staffUserId, staffUserName } = args;
  const projectId = project.id;
  if (!projectId) throw new Error('RFQ project is missing.');

  const label = input.quoteLabel.trim();
  if (!label) throw new Error('Enter a quote label.');
  const fileError = validateLegacyQuoteFile(input.file);
  if (fileError) throw new Error(fileError);
  if (!isValidQuoteTotal(input.quoteTotal)) {
    throw new Error('Enter a valid quote amount.');
  }
  if (!isPurchaseJobPlBucket(input.plBucket)) {
    throw new Error('Pick a proposal node.');
  }
  if (!isOtherQuotePayMethodId(input.paymentMethodId)) {
    throw new Error('Choose Company Card or Bill Payment.');
  }
  assertOtherQuoteJobPurchaseReady({
    requesterId: staffUserId,
    users: args.users,
    policy: args.policy,
    dealId: project.dealId,
    amount: input.quoteTotal,
    paymentMethodId: input.paymentMethodId,
    projectManagerUserId: args.projectManagerUserId,
  });

  const packageId = await ensureOtherQuotePackage(
    deps,
    project,
    packages,
    orgId,
    label,
    staffUserId,
    staffUserName,
  );

  const { storagePath, downloadUrl } = await uploadLegacyQuoteFile(orgId, packageId, input.file);
  const paymentMethod =
    OTHER_QUOTE_PAYMENT_METHODS.find(m => m.id === input.paymentMethodId)?.label
    || (input.paymentMethodId === 'ach_office' ? 'Bill Payment' : 'Company Card');

  const submissionId = await deps.createRfqSubmission(
    {
      rfqPackageId: packageId,
      rfqProjectId: projectId,
      supplierOrgId: orgId,
      dealId: project.dealId,
      workType: 'other_quote',
      fileName: input.file.name,
      storagePath,
      downloadUrl,
      quoteTotal: input.quoteTotal,
      quoteLabel: label,
      quoteValidUntil: input.quoteValidUntil ?? undefined,
      supplierOrgName: orgName.trim(),
      paymentTermsDraft: input.paymentTermsDraft,
      plBucket: input.plBucket,
      paymentMethodId: input.paymentMethodId,
      paymentMethod,
      staffManualUpload: true,
      notes: input.notes?.trim() || undefined,
      contentType: input.file.type || 'application/pdf',
      sizeBytes: input.file.size,
      uploadedByUid: staffUserId,
      uploadedByName: staffUserName,
    },
    { createdAt: input.receivedAt ?? new Date() },
  );

  const submitLinked =
    deps.submitLinkedJobPurchase
    || (await import('./otherQuotePurchaseSubmit')).submitOtherQuoteJobPurchase;
  const linked = await submitLinked({
    requesterId: staffUserId,
    requesterName: staffUserName,
    users: args.users,
    policy: args.policy,
    dealId: project.dealId,
    dealTitle: args.dealTitle,
    timberProjectId: args.timberProjectId || project.timberProjectId,
    projectManagerUserId: args.projectManagerUserId,
    vendorName: orgName,
    quoteLabel: label,
    amount: input.quoteTotal,
    notes: input.notes,
    plBucket: input.plBucket,
    paymentMethodId: input.paymentMethodId,
    rfqSubmissionId: submissionId,
    rfqProjectId: projectId,
    quoteAttachment: {
      name: input.file.name,
      storagePath,
      url: downloadUrl,
      contentType: input.file.type || 'application/pdf',
      size: input.file.size,
      kind: 'quote',
    },
    afterCreate: purchaseRequisitionId =>
      deps.updateRfqSubmission(submissionId, { purchaseRequisitionId }),
  });

  if (project.status !== 'quoted' && project.status !== 'closed' && !linked.issued) {
    await deps.updateRfqProject(projectId, { status: 'quoted' });
  }

  return {
    submissionId,
    purchaseRequisitionId: linked.purchaseRequisitionId,
    issued: linked.issued,
    paymentMethodId: linked.paymentMethodId,
    paymentMethod: linked.paymentMethod,
  };
}
